import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { beep } from "@/lib/sound";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Paperclip, FileText, X } from "lucide-react";
import { fmtDateShort } from "@/components/shared/DashKit";
import { AttachmentList, uploadTicketFiles, ACCEPT, MAX_FILES, MAX_MB } from "@/components/shared/SupportAttachments";
import { MsgTicks, newClientMsgId, sendWithRetryId } from "@/components/support/MsgTicks";
import { useTr } from "@/lib/tr";

const SERVE = "/support/attachments";

// Floating quick-chat: feeds a single "chat" support ticket (unified ticketing).
export function ChatPanel({ onSynced }) {
  const { t } = useContent();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState([]);
  const tr2 = useTr();
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const prevStaffCount = useRef(null);

  const load = async () => {
    const { data: d } = await api.get("/support/quick");
    const msgs = d.messages || [];
    const staffCount = msgs.filter((m) => m.sender_role !== "customer").length;
    if (prevStaffCount.current !== null && staffCount > prevStaffCount.current) beep();
    prevStaffCount.current = staffCount;
    setData(d);
    onSynced && onSynced(0);
  };
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const merged = [...files];
    for (const f of picked) {
      if (merged.length >= MAX_FILES) { toast.error(`${t("chat.max_files", "Maximum")} ${MAX_FILES}`); break; }
      if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name} > ${MAX_MB} Mo`); continue; }
      merged.push(f);
    }
    setFiles(merged);
  };

  const post = async (p) => {
    const { data: r } = await api.post("/support/quick/message", { message: p.message, client_msg_id: p.cid });
    if (p.files?.length && r.ticket_id && r.message_id) {
      try { await uploadTicketFiles(p.files, `/support/tickets/${r.ticket_id}/attachments`, r.message_id); }
      catch { toast.error(t("chat.att_error", "Échec de l'envoi d'une pièce jointe")); }
    }
    await load();
  };
  const retry = (p) => sendWithRetryId(post, p, setPending).catch(() => {});
  const send = async () => {
    if (!text.trim() && !files.length) return;
    const p = { cid: newClientMsgId(), message: text.trim() || "(pièce jointe)", files, created_at: new Date().toISOString() };
    setText(""); setFiles([]);
    setSending(true);
    try { await sendWithRetryId(post, p, setPending); } catch { /* failed state with retry */ } finally { setSending(false); }
  };

  const msgs = [...(data?.messages || []), ...pending.filter((p) => !(data?.messages || []).some((m) => m.client_msg_id === p.cid)).map((p) => ({ ...p, id: p.cid, sender_role: "customer" }))];
  const resolved = data?.ticket?.status === "resolved";
  const lastRole = msgs.length ? msgs[msgs.length - 1].sender_role : null;
  const waiting = !resolved && !data?.agent && (lastRole === "customer" || !msgs.length);

  return (
    <div className="flex h-full flex-col" data-testid="chat-panel">
      <div className="flex-1 space-y-3 overflow-y-auto overscroll-contain p-4">
        {!data ? <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-accent" /></div> : (
          <>
            {!msgs.length && <div className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">{t("chat.empty", "Envoyez un message, il crée automatiquement un ticket suivi par notre équipe.")}</div>}
            {msgs.map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.sender_role === "customer" ? "ml-auto bg-accent text-white" : "bg-secondary text-foreground"}`}>
                <div className="mb-0.5 text-[10px] font-medium opacity-70">{m.sender_role === "customer" ? t("chat.you", "Vous") : `${m.sender_name || "Support"} · ${tr2("Support TWP", "TWP Support")}`} · {fmtDateShort(m.created_at)}</div>
                {m.message}
                <AttachmentList attachments={m.attachments} serveBase={SERVE} />
                {m.sender_role === "customer" && <MsgTicks m={m} tr={tr2} onRetry={retry} />}
              </div>
            ))}
            {data?.agent && <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground" data-testid="chat-agent"><span className={`h-2 w-2 rounded-full ${data.agent.online ? "bg-emerald-500" : "bg-slate-400"}`} />{data.agent.first_name} · {tr2("Support TWP", "TWP Support")}</div>}
            {resolved && <div className="flex items-center justify-center gap-1.5 text-xs text-emerald-500"><CheckCircle2 className="h-4 w-4" /> {t("chat.resolved", "Ticket marqué comme résolu")}</div>}
            {waiting && msgs.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="chat-waiting">
                {t("chat.waiting", "En attente d'un agent")}
                <span className="flex gap-0.5">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.3s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground [animation-delay:-0.15s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground" />
                </span>
              </div>
            )}
            <div ref={endRef} />
          </>
        )}
      </div>
      <div className="border-t border-border p-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        {files.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5" data-testid="chat-att-preview">
            {files.map((f, i) => (
              <span key={i} data-testid={`chat-att-chip-${i}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 px-2.5 py-1 text-xs">
                <FileText className="h-3 w-3" /> <span className="max-w-[140px] truncate">{f.name}</span>
                <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} data-testid={`chat-att-remove-${i}`}><X className="h-3 w-3 opacity-60 hover:opacity-100" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={onPick} data-testid="chat-att-input" />
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="chat-att-btn" title={t("chat.attach", "Joindre un fichier")} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-secondary/60 sm:h-10 sm:w-10">
            <Paperclip className="h-4 w-4" />
          </button>
          <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={t("chat.input_ph", "Écrivez votre message...")} className="min-w-0 flex-1 rounded-full border border-border bg-secondary/50 px-4 py-2.5 text-base outline-none focus:ring-2 focus:ring-accent/40 sm:text-sm" />
          <button onClick={send} disabled={sending || (!text.trim() && !files.length)} data-testid="chat-send-btn" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-accent disabled:opacity-50 sm:h-10 sm:w-10">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
